import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RotateCcw, Download, Upload } from 'lucide-react';

interface SignaturePadProps {
  onSignatureChange: (signatureData: string | null) => void;
  width?: number;
  height?: number;
}

export const SignaturePadComponent: React.FC<SignaturePadProps> = ({
  onSignatureChange,
  width = 400,
  height = 200
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = width;
      canvas.height = height;

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 2.5,
        throttle: 16
      });

      // Event listeners
      signaturePadRef.current.addEventListener('beginStroke', () => {
        setIsDrawing(true);
      });

      signaturePadRef.current.addEventListener('endStroke', () => {
        setIsDrawing(false);
        updateSignatureData();
      });

      return () => {
        if (signaturePadRef.current) {
          signaturePadRef.current.removeEventListener('beginStroke', () => {});
          signaturePadRef.current.removeEventListener('endStroke', () => {});
        }
      };
    }
  }, [width, height]);

  const updateSignatureData = () => {
    if (signaturePadRef.current) {
      if (signaturePadRef.current.isEmpty()) {
        onSignatureChange(null);
      } else {
        const signatureData = signaturePadRef.current.toDataURL('image/png');
        onSignatureChange(signatureData);
      }
    }
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onSignatureChange(null);
    }
  };

  const downloadSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const link = document.createElement('a');
      link.download = 'assinatura.png';
      link.href = signaturePadRef.current.toDataURL('image/png');
      link.click();
    }
  };

  const uploadSignature = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && signaturePadRef.current) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              updateSignatureData();
            }
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">
          🖊️ Assinatura Digital
        </CardTitle>
        <p className="text-sm text-gray-600">
          Assine no campo abaixo usando o mouse ou touch
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
          <canvas
            ref={canvasRef}
            className="border border-gray-200 rounded cursor-crosshair"
            style={{ width: `${width}px`, height: `${height}px` }}
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearSignature}
            disabled={!signaturePadRef.current || signaturePadRef.current.isEmpty()}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Limpar
          </Button>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadSignature}
            disabled={!signaturePadRef.current || signaturePadRef.current.isEmpty()}
          >
            <Download className="w-4 h-4 mr-2" />
            Baixar
          </Button>
          
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              onChange={uploadSignature}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="relative"
            >
              <Upload className="w-4 h-4 mr-2" />
              Carregar
            </Button>
          </div>
        </div>
        
        {isDrawing && (
          <div className="text-sm text-blue-600 animate-pulse">
            ✍️ Assinando...
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          <p>💡 Dicas:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Use o mouse ou touch para assinar</li>
            <li>Clique em "Limpar" para recomeçar</li>
            <li>Você pode carregar uma imagem de assinatura</li>
            <li>A assinatura será salva automaticamente</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
